import { useMemo, useState } from 'react';
import { Calculator, TrendingDown, Sparkles, ArrowRight } from 'lucide-react';
import { Slider } from './ui/slider';
import { Button } from './ui/button';

/**
 * Interactive ROI Calculator — shows the user exactly how much they save
 * per month using Around the World AI vs assembling their own stack.
 */
export default function ROICalculator({ onCta }) {
  const [projects, setProjects] = useState([3]);
  const [team, setTeam] = useState([2]);
  const [conversations, setConversations] = useState([300]);

  const p = projects[0];
  const t = team[0];
  const c = conversations[0];

  const numbers = useMemo(() => {
    // DIY costs per month
    const infra = 60 * p + 80;                 // hosting per app + baseline
    const llm = Math.max(40, c * 0.35);        // ~$0.35/conv average across providers
    const tools = 55 * t + 90;                 // GitHub, Sentry, etc + baseline
    const engineer = t * 6500;                 // fully-loaded eng seat (fractional at this level)
    const total = infra + llm + tools + engineer;

    // Our plan recommendation
    let ourPlan, ourCost, ourCredits;
    if (c <= 100 && p <= 1) {
      ourPlan = 'Standard';
      ourCost = 17;
      ourCredits = 100;
    } else if (c <= 500 && p <= 5) {
      ourPlan = 'Pro';
      ourCost = 84;
      ourCredits = 550;
    } else {
      ourPlan = 'Scale';
      ourCost = 420;
      ourCredits = 3000;
    }

    const saveMonthly = total - ourCost;
    const saveAnnual = saveMonthly * 12;

    return { infra, llm, tools, engineer, total, ourPlan, ourCost, ourCredits, saveMonthly, saveAnnual };
  }, [p, t, c]);

  return (
    <section
      id="roi"
      className="relative py-24 md:py-28 text-white"
      style={{
        background:
          'radial-gradient(900px 500px at 50% 20%, rgba(0,240,255,0.06) 0%, transparent 60%),' +
          'linear-gradient(180deg, #000 0%, #030303 100%)',
      }}
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.4em] text-[#FF007F] font-semibold uppercase mb-4 font-mono">
            <Calculator size={12} />
            ROI calculator
          </div>
          <h2 className="font-display text-4xl md:text-6xl font-black tracking-[-0.03em] text-white">
            See how much{' '}
            <span className="italic bg-clip-text text-transparent bg-gradient-to-r from-[#00F0FF] to-[#FF007F]">
              you&rsquo;ll save.
            </span>
          </h2>
          <p className="mt-5 text-white/60 text-lg leading-relaxed">
            Move the sliders. We&rsquo;ll show you exactly what a DIY stack costs
            &mdash; and how much Around the World AI saves you every month.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-5 gap-8">
          {/* Left: sliders */}
          <div className="lg:col-span-3 glass rounded-2xl p-7">
            <SliderRow
              label="How many apps / projects do you plan to run?"
              value={p}
              onChange={setProjects}
              min={1}
              max={20}
              step={1}
              format={(v) => `${v} app${v > 1 ? 's' : ''}`}
            />
            <SliderRow
              label="How many people on your team?"
              value={t}
              onChange={setTeam}
              min={1}
              max={20}
              step={1}
              format={(v) => `${v} person${v > 1 ? 's' : ''}`}
            />
            <SliderRow
              label="AI conversations per month"
              value={c}
              onChange={setConversations}
              min={50}
              max={5000}
              step={50}
              format={(v) => `${v.toLocaleString()} conv/mo`}
            />

            {/* DIY breakdown */}
            <div className="mt-6 rounded-xl border border-white/10 bg-black/40 p-4">
              <div className="text-[11px] font-bold tracking-widest text-white/50 mb-2 font-mono uppercase">
                DIY tech stack cost
              </div>
              <div className="grid grid-cols-2 gap-y-1.5 text-[13px]">
                <span className="text-white/60">Infrastructure (hosting, DB, CDN)</span>
                <span className="text-right font-semibold text-white">${numbers.infra.toLocaleString()}</span>
                <span className="text-white/60">LLM APIs (Claude / GPT / Gemini)</span>
                <span className="text-right font-semibold text-white">${Math.round(numbers.llm).toLocaleString()}</span>
                <span className="text-white/60">Dev tools (GitHub, Sentry, etc.)</span>
                <span className="text-right font-semibold text-white">${numbers.tools.toLocaleString()}</span>
                <span className="text-white/60">Engineer time (fully loaded)</span>
                <span className="text-right font-semibold text-white">${numbers.engineer.toLocaleString()}</span>
                <span className="col-span-2 border-t border-white/10 mt-1 pt-1.5 flex justify-between font-bold text-white">
                  <span>Total monthly cost</span>
                  <span className="text-rose-300">${numbers.total.toLocaleString()}</span>
                </span>
              </div>
            </div>
          </div>

          {/* Right: savings card */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl overflow-hidden pro-border-glow shadow-[0_35px_80px_-30px_rgba(0,240,255,0.4)]">
              <div
                className="p-7 text-white relative"
                style={{
                  background:
                    'linear-gradient(160deg, #030303 0%, #0a0a0a 55%, #1a0a1a 130%)',
                }}
              >
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#00F0FF]/15 border border-[#00F0FF]/35 text-[#00F0FF] text-[10px] font-bold tracking-widest uppercase font-mono">
                  <Sparkles size={11} /> Recommended for you
                </div>
                <div className="mt-4">
                  <div className="text-[13px] text-white/60">
                    Around the World AI &mdash; {numbers.ourPlan}
                  </div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="font-display text-5xl font-black text-white">${numbers.ourCost}</span>
                    <span className="text-white/60">/ mo</span>
                  </div>
                  <div className="mt-1 text-[13px] text-white/70">
                    {numbers.ourCredits.toLocaleString()} credits / month
                  </div>
                </div>

                <div className="mt-6 pt-5 border-t border-white/10">
                  <div className="flex items-center gap-2 text-[#FF007F] text-[10px] font-bold uppercase tracking-widest font-mono">
                    <TrendingDown size={14} /> You save
                  </div>
                  <div className="mt-1 font-display text-3xl font-black text-white">
                    ${numbers.saveMonthly.toLocaleString()}
                    <span className="text-white/50 text-lg font-normal"> / month</span>
                  </div>
                  <div className="mt-1 text-white/70 text-[13px]">
                    That&rsquo;s{' '}
                    <span className="font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#00F0FF] to-[#FF007F]">
                      ${numbers.saveAnnual.toLocaleString()}
                    </span>{' '}
                    saved every year.
                  </div>
                </div>

                <Button
                  onClick={onCta}
                  className="mt-6 w-full h-11 rounded-xl bg-gradient-to-r from-[#00F0FF] to-[#FF007F] text-black hover:brightness-110 font-bold shadow-lg"
                >
                  Start with {numbers.ourPlan} <ArrowRight size={16} className="ml-1" />
                </Button>
              </div>
              <div className="bg-black/60 p-4 text-center text-[11px] text-white/50 border-t border-white/8 font-mono uppercase tracking-widest">
                Cancel anytime · Credits never expire · No card for Free tier
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SliderRow({ label, value, onChange, min, max, step, format }) {
  return (
    <div className="mb-6 last:mb-0">
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-white/85">{label}</label>
        <span className="text-sm font-bold text-[#00F0FF] bg-black/50 border border-white/10 rounded-lg px-2 py-0.5 font-mono">
          {format(value)}
        </span>
      </div>
      <Slider
        min={min}
        max={max}
        step={step}
        value={[value]}
        onValueChange={onChange}
        className="cursor-pointer"
      />
    </div>
  );
}
